import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Upload, X, Copy, Search, FileText, Link2, Users, Eye, GitBranch, Lock, Check } from 'lucide-react';
import { toast } from 'sonner';
import { COVER_IMAGES } from './LanguageCard';
import { API_BASE as API } from '../../config/api';
import axios from 'axios';

// Create Language Dialog with mode selector
export const CreateLanguageDialog = ({ 
  open, 
  onOpenChange, 
  onSubmit,
  onClone,
  loading 
}) => {
  const [mode, setMode] = useState('blank'); // blank, clone, template
  const [name, setName] = useState('');
  const [nativeName, setNativeName] = useState('');
  const [description, setDescription] = useState('');
  const [creator, setCreator] = useState('');
  // Clone state
  const [cloneSearch, setCloneSearch] = useState('');
  const [cloneResults, setCloneResults] = useState([]);
  const [selectedClone, setSelectedClone] = useState(null);
  const [cloneLoading, setCloneLoading] = useState(false);

  useEffect(() => {
    if (mode === 'clone' && !cloneResults.length) fetchCloneable('');
  }, [mode]);

  const fetchCloneable = async (q) => {
    setCloneLoading(true);
    try {
      // Fetch user's own + shared + public (with allow_cloning)
      const [own, shared] = await Promise.all([
        axios.get(`${API}/conlangs`, { withCredentials: true }),
        axios.get(`${API}/conlangs/shared-with-me`, { withCredentials: true }),
      ]);
      let all = [...(own.data || []), ...(shared.data || [])];
      // Deduplicate
      const seen = new Set();
      all = all.filter(c => { if (seen.has(c.conlang_id)) return false; seen.add(c.conlang_id); return true; });
      if (q) {
        const lower = q.toLowerCase();
        all = all.filter(c => c.name.toLowerCase().includes(lower));
      }
      setCloneResults(all);
    } catch { /* silent */ }
    finally { setCloneLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'blank') {
      await onSubmit({ name, native_name: nativeName, description, creator });
    } else if (mode === 'clone' && selectedClone) {
      await onClone(selectedClone.conlang_id);
    }
    resetForm();
  };

  const resetForm = () => {
    setName(''); setNativeName(''); setDescription(''); setCreator('');
    setMode('blank'); setCloneSearch(''); setCloneResults([]); setSelectedClone(null);
  };

  const handleClose = () => { onOpenChange(false); resetForm(); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground">Create New Language</DialogTitle>
          <DialogDescription className="text-foreground-muted">Choose how to start your new language.</DialogDescription>
        </DialogHeader>
        {/* Mode Tabs */}
        <div className="flex gap-2 mb-2" data-testid="create-mode-tabs">
          {[
            { id: 'blank', label: 'New Blank', icon: FileText },
            { id: 'clone', label: 'Clone Existing', icon: Copy },
            { id: 'template', label: 'From Template', icon: GitBranch },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} type="button"
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors border ${mode === id ? 'bg-primary/10 border-primary text-primary' : 'border-border text-foreground-muted hover:bg-accent'}`}
              onClick={() => setMode(id)}
              data-testid={`create-mode-${id}`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>
        
        {mode === 'blank' && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="create-name" className="text-foreground">Name</Label>
              <Input id="create-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Elvish" className="bg-background border-border" required data-testid="create-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-native-name" className="text-foreground">Native Name (Optional)</Label>
              <Input id="create-native-name" value={nativeName} onChange={(e) => setNativeName(e.target.value)} placeholder="e.g., Quendya" className="bg-background border-border font-mono" data-testid="create-native-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-description" className="text-foreground">Short Description (Optional)</Label>
              <Input id="create-description" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 60))} placeholder="A brief tagline..." className="bg-background border-border" maxLength={60} data-testid="create-description-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-creator" className="text-foreground">Creator (Optional)</Label>
              <Input id="create-creator" value={creator} onChange={(e) => setCreator(e.target.value)} placeholder="e.g., J.R.R. Tolkien" className="bg-background border-border" data-testid="create-creator-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} className="border-border">Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading || !name.trim()} data-testid="create-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {mode === 'clone' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" />
              <Input value={cloneSearch} onChange={e => { setCloneSearch(e.target.value); fetchCloneable(e.target.value); }} placeholder="Search your languages..." className="bg-background border-border pl-9" data-testid="clone-search" />
            </div>
            <div className="max-h-[240px] overflow-y-auto space-y-1 border border-border rounded-md p-2" data-testid="clone-results">
              {cloneLoading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
              {!cloneLoading && cloneResults.length === 0 && <p className="text-foreground-subtle text-sm text-center py-4">No languages found</p>}
              {cloneResults.map(c => (
                <button key={c.conlang_id} type="button"
                  className={`w-full text-left p-2.5 rounded-md transition-colors flex items-center gap-3 ${selectedClone?.conlang_id === c.conlang_id ? 'bg-primary/10 border border-primary' : 'hover:bg-accent border border-transparent'}`}
                  onClick={() => setSelectedClone(c)}
                  data-testid={`clone-option-${c.conlang_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground text-sm font-medium truncate">{c.name}</p>
                    <p className="text-foreground-subtle text-xs truncate">{c.description || 'No description'}</p>
                  </div>
                  {c.access_level !== 'owner' && <span className="text-xs text-foreground-subtle bg-accent px-1.5 py-0.5 rounded">{c.owner_name || 'Shared'}</span>}
                  {selectedClone?.conlang_id === c.conlang_id && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                </button>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} className="border-border">Cancel</Button>
              <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90" disabled={loading || !selectedClone} data-testid="clone-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Clone Language'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {mode === 'template' && (
          <div className="py-8 text-center">
            <GitBranch className="w-10 h-10 text-foreground-subtle mx-auto mb-3" />
            <p className="text-foreground-muted text-sm">Templates coming soon.</p>
            <p className="text-foreground-subtle text-xs mt-1">Pre-built language structures for common conlang types.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Share Language Dialog
export const ShareDialog = ({ open, onOpenChange, conlang }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [friends, setFriends] = useState([]);
  const [searching, setSearching] = useState(false);
  const [accessLevel, setAccessLevel] = useState('read');
  const [sharing, setSharing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [currentShares, setCurrentShares] = useState([]);

  useEffect(() => {
    if (open && conlang) {
      fetchCurrentShares();
      fetchFriends();
      fetchShareLink();
    }
  }, [open, conlang]);

  const fetchCurrentShares = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlang.conlang_id}/shares`, { withCredentials: true });
      setCurrentShares(res.data || []);
    } catch { /* silent */ }
  };

  const fetchFriends = async () => {
    try {
      const res = await axios.get(`${API}/social/friends`, { withCredentials: true });
      setFriends(res.data || []);
    } catch { /* silent */ }
  };

  const fetchShareLink = async () => {
    // Check if a link already exists from the conlang data
    // We'll try generating one to check
  };

  const handleSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/users/search`, { params: { q }, withCredentials: true });
      setResults(res.data || []);
    } catch { /* silent */ }
    finally { setSearching(false); }
  };

  const handleShare = async (userId) => {
    setSharing(true);
    try {
      await axios.post(`${API}/conlangs/${conlang.conlang_id}/shares`, {
        user_id: userId, access_level: accessLevel
      }, { withCredentials: true });
      toast.success('Language shared!');
      fetchCurrentShares();
      setSearch(''); setResults([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to share');
    } finally { setSharing(false); }
  };

  const handleRevoke = async (userId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlang.conlang_id}/shares/${userId}`, { withCredentials: true });
      toast.success('Access revoked');
      fetchCurrentShares();
    } catch { toast.error('Failed to revoke access'); }
  };

  const handleUpdateAccess = async (userId, newLevel) => {
    try {
      await axios.put(`${API}/conlangs/${conlang.conlang_id}/shares/${userId}`, { access_level: newLevel }, { withCredentials: true });
      toast.success('Access updated');
      fetchCurrentShares();
    } catch { toast.error('Failed to update'); }
  };

  const handleGenerateLink = async () => {
    setLinkLoading(true);
    try {
      const res = await axios.post(`${API}/conlangs/${conlang.conlang_id}/share-link`, {}, { withCredentials: true });
      setShareLink(res.data.token);
    } catch { toast.error('Failed to generate link'); }
    finally { setLinkLoading(false); }
  };

  const handleRevokeLink = async () => {
    try {
      await axios.delete(`${API}/conlangs/${conlang.conlang_id}/share-link`, { withCredentials: true });
      setShareLink(null);
      toast.success('Link revoked');
    } catch { toast.error('Failed to revoke link'); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/shared/${shareLink}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };

  const accessOptions = [
    { value: 'read', label: 'View Only', icon: Eye, desc: 'Can view but not edit or clone' },
    { value: 'clone', label: 'Clone', icon: Copy, desc: 'Can clone to their own copy' },
    { value: 'collaborate', label: 'Collaborate', icon: Users, desc: 'Can edit the language directly' },
  ];

  if (!conlang) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground">Share "{conlang.name}"</DialogTitle>
          <DialogDescription className="text-foreground-muted">Share with specific users or generate a view-only link.</DialogDescription>
        </DialogHeader>

        {/* Permission Level */}
        <div className="space-y-2">
          <Label className="text-foreground text-sm">Permission Level</Label>
          <div className="grid grid-cols-3 gap-2">
            {accessOptions.map(opt => (
              <button key={opt.value} type="button"
                className={`p-2 rounded-md text-center text-xs border transition-colors ${accessLevel === opt.value ? 'bg-primary/10 border-primary text-primary' : 'border-border text-foreground-muted hover:bg-accent'}`}
                onClick={() => setAccessLevel(opt.value)}
                data-testid={`access-${opt.value}`}
              >
                <opt.icon className="w-4 h-4 mx-auto mb-1" />
                <p className="font-medium">{opt.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* User Search */}
        <div className="space-y-2">
          <Label className="text-foreground text-sm">Share with a user</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" />
            <Input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search by name or email..." className="bg-background border-border pl-9" data-testid="share-search" />
          </div>
          {results.length > 0 && (
            <div className="max-h-[120px] overflow-y-auto space-y-1 border border-border rounded-md p-1">
              {results.map(u => (
                <button key={u.user_id} type="button" onClick={() => handleShare(u.user_id)}
                  className="w-full text-left p-2 rounded hover:bg-accent flex items-center justify-between" disabled={sharing}
                  data-testid={`share-user-${u.user_id}`}
                >
                  <div>
                    <p className="text-foreground text-sm font-medium">{u.name}</p>
                    <p className="text-foreground-subtle text-xs">{u.email}</p>
                  </div>
                  <span className="text-primary text-xs">Share</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Friends Quick Select */}
        {friends.length > 0 && !search && (
          <div className="space-y-2">
            <Label className="text-foreground text-sm">Friends</Label>
            <div className="flex flex-wrap gap-1.5">
              {friends.slice(0, 8).map(f => (
                <button key={f.user_id} type="button" onClick={() => handleShare(f.user_id)}
                  className="text-xs bg-accent text-foreground-muted px-2.5 py-1 rounded-full hover:bg-accent/80 transition-colors"
                  disabled={sharing}
                  data-testid={`share-friend-${f.user_id}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Current Shares */}
        {currentShares.length > 0 && (
          <div className="space-y-2">
            <Label className="text-foreground text-sm">Currently Shared With</Label>
            <div className="space-y-1">
              {currentShares.map(s => (
                <div key={s.user_id} className="flex items-center justify-between p-2 bg-accent/50 rounded-md">
                  <div>
                    <p className="text-foreground text-sm font-medium">{s.name}</p>
                    <p className="text-foreground-subtle text-xs">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={s.access_level} onChange={e => handleUpdateAccess(s.user_id, e.target.value)}
                      className="bg-background border border-border rounded text-xs px-1.5 py-1 text-foreground"
                    >
                      <option value="read">View Only</option>
                      <option value="clone">Clone</option>
                      <option value="collaborate">Collaborate</option>
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => handleRevoke(s.user_id)} className="text-destructive hover:text-destructive h-7 px-1.5">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Share Link */}
        <div className="border-t border-border pt-3 space-y-2">
          <Label className="text-foreground text-sm flex items-center gap-1.5"><Link2 className="w-4 h-4" /> View-Only Link</Label>
          {shareLink ? (
            <div className="flex items-center gap-2">
              <Input value={`${window.location.origin}/shared/${shareLink}`} readOnly className="bg-background border-border text-xs flex-1" />
              <Button size="sm" variant="outline" onClick={copyLink} className="h-8" data-testid="copy-link-btn"><Copy className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={handleRevokeLink} className="text-destructive h-8" data-testid="revoke-link-btn"><X className="w-3 h-3" /></Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={handleGenerateLink} disabled={linkLoading} className="border-border" data-testid="generate-link-btn">
              {linkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Link2 className="w-3 h-3 mr-1" />} Generate Link
            </Button>
          )}
          <p className="text-xs text-foreground-subtle">Anyone with this link can view the language (read-only).</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Language Dialog
export const EditLanguageDialog = ({ 
  open, 
  onOpenChange, 
  conlang,
  onSubmit, 
  loading 
}) => {
  const [name, setName] = useState(conlang?.name || '');
  const [nativeName, setNativeName] = useState(conlang?.native_name || '');
  const [description, setDescription] = useState(conlang?.description || '');

  // Update form when conlang changes
  React.useEffect(() => {
    if (conlang) {
      setName(conlang.name || '');
      setNativeName(conlang.native_name || '');
      setDescription(conlang.description || '');
    }
  }, [conlang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit({ name, native_name: nativeName, description });
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-background-paper border-border" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground">Edit Language</DialogTitle>
          <DialogDescription className="text-foreground-muted">Update your language's details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name" className="text-foreground">Name</Label>
            <Input 
              id="edit-name" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              className="bg-background border-border" 
              required 
              data-testid="edit-name-input" 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-native-name" className="text-foreground">Native Name</Label>
            <Input 
              id="edit-native-name" 
              value={nativeName} 
              onChange={(e) => setNativeName(e.target.value)} 
              className="bg-background border-border font-mono" 
              data-testid="edit-native-name-input" 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description" className="text-foreground">Short Description</Label>
            <Input 
              id="edit-description" 
              value={description} 
              onChange={(e) => setDescription(e.target.value.slice(0, 60))} 
              placeholder="A brief tagline for your language..."
              className="bg-background border-border" 
              maxLength={60}
              data-testid="edit-description-input" 
            />
            <p className="text-xs text-foreground-subtle text-right">{description.length}/60</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="border-border">Cancel</Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading || !name.trim()} data-testid="edit-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Language Dialog
export const DeleteLanguageDialog = ({ 
  open, 
  onOpenChange, 
  conlang,
  onConfirm, 
  loading 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-background-paper border-border" data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground">Delete Language</DialogTitle>
          <DialogDescription className="text-foreground-muted">
            Are you sure you want to delete <span className="text-foreground font-medium">{conlang?.name}</span>? This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} data-testid="delete-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Cover Image Dialog
export const CoverImageDialog = ({ 
  open, 
  onOpenChange, 
  conlang,
  onSelectCover,
  loading 
}) => {
  const [customPreview, setCustomPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCustomPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = (e) => {
    processFile(e.target.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    processFile(file);
  };

  const handleApplyCustom = () => {
    if (customPreview) {
      onSelectCover(customPreview);
    }
  };

  const clearPreview = () => {
    setCustomPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) clearPreview();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="cover-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground">Choose Cover Image</DialogTitle>
          <DialogDescription className="text-foreground-muted">
            Select a preset or upload your own image (recommended: 1200x675px, max 2MB).
          </DialogDescription>
        </DialogHeader>
        
        {/* Custom Upload Section */}
        <div className="space-y-3 pb-4 border-b border-border">
          <Label className="text-foreground">Upload Custom Image</Label>
          {customPreview ? (
            <div className="relative">
              <img 
                src={customPreview} 
                alt="Preview" 
                className="w-full h-32 object-cover rounded-lg"
                data-testid="custom-cover-preview"
              />
              <button
                onClick={clearPreview}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                data-testid="clear-custom-cover-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <Button 
                onClick={handleApplyCustom}
                disabled={loading}
                className="w-full mt-2"
                data-testid="apply-custom-cover-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Apply This Image
              </Button>
            </div>
          ) : (
            <div 
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragging 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid="upload-cover-area"
            >
              <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className="text-sm text-muted-foreground">
                {isDragging ? 'Drop image here' : 'Drag & drop or click to upload'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP • Max 2MB</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="cover-file-input"
          />
        </div>

        {/* Preset Gradients */}
        <div className="space-y-3 pt-2">
          <Label className="text-foreground">Or Choose a Preset</Label>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(COVER_IMAGES).map(([key, gradient]) => (
              <button
                key={key}
                onClick={() => onSelectCover(key)}
                disabled={loading}
                className={`h-16 rounded-lg transition-all ${
                  conlang?.cover_image === key 
                    ? 'ring-2 ring-primary ring-offset-2 ring-offset-background-paper' 
                    : 'hover:opacity-80'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ background: gradient }}
                data-testid={`cover-option-${key}`}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
